import React, { useState } from 'react'
import { Disclosure, Transition, Popover } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { NavigationGroupProps } from '../types'

export const NavigationGroup: React.FC<NavigationGroupProps> = ({
  group,
  isRailCollapsed,
  isMobile,
  isExpanded,
  onToggle,
  onNavigate,
  activeTabPath,
  renderIcon,
  getBadgeValue,
  isAdmin
}) => {
  const [isHovering, setIsHovering] = useState(false)

  // Permission check helper
  const hasTabPermission = (tab: typeof group.children[0]) => {
    if (!tab.permissions) return true
    return tab.permissions.every((permission) => {
      if (permission === 'admin_only') return isAdmin
      return true
    })
  }

  // Check if any child is active
  const hasActiveChild = group.children.some((child) =>
    activeTabPath.startsWith(child.path)
  )

  // Calculate aggregate badge count from visible children only
  const groupBadgeCount = group.children
    .filter((child) => !child.hidden && hasTabPermission(child))
    .reduce((sum, child) => {
      const badgeValue = getBadgeValue?.(child.id)
      return sum + (badgeValue ? Number(badgeValue) : 0)
    }, 0)

  // Collapsed rail behavior: show flyout popover on hover
  if (isRailCollapsed && !isMobile) {
    return (
      <li
        className="menu-group-collapsed"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <Popover className="relative">
          <Popover.Button
            className="menu-button menu-group-icon-only"
          >
            <span className="menu-icon">{renderIcon(group.icon)}</span>
            {groupBadgeCount > 0 && <span className="group-badge-dot" />}
          </Popover.Button>

          <Transition
            show={isHovering}
            enter="transition-opacity duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Popover.Panel
              static
              className="menu-flyout"
            >
              <div className="menu-flyout-header">
                {group.label}
              </div>
              <div className="menu-flyout-items">
                {group.children
                  .filter((child) => !child.hidden && hasTabPermission(child))
                  .map((child) => {
                    const isActive = activeTabPath.startsWith(child.path)
                    const childBadgeValue = getBadgeValue?.(child.id)

                    return (
                      <button
                        key={child.id}
                        onClick={() => onNavigate(child.path)}
                        className={`menu-flyout-item ${isActive ? 'active' : ''}`}
                      >
                        <span className="menu-flyout-item-icon">{renderIcon(child.icon)}</span>
                        <span className="menu-flyout-item-label">{child.label}</span>
                        {childBadgeValue && (
                          <span className="menu-flyout-badge">
                            {childBadgeValue}
                          </span>
                        )}
                      </button>
                    )
                  })}
              </div>
            </Popover.Panel>
          </Transition>
        </Popover>
      </li>
    )
  }

  return (
    <li className="menu-group">
      <Disclosure as="div" defaultOpen={isExpanded}>
        {() => (
          <>
            <Disclosure.Button
              as="button"
              onClick={onToggle}
              className="menu-group-button"
              aria-expanded={isExpanded}
              aria-controls={`nav-group-${group.id}-panel`}
            >
              <span className="menu-icon">{renderIcon(group.icon)}</span>
              <span className="menu-label">{group.label}</span>
              {groupBadgeCount > 0 && (
                <span className="group-badge">{groupBadgeCount}</span>
              )}
              <ChevronDownIcon
                className={`menu-chevron ${isExpanded ? 'rotate-180' : ''}`}
              />
            </Disclosure.Button>

            <Transition
              show={isExpanded}
              enter="transition-all duration-200 ease-out"
              enterFrom="opacity-0 max-h-0"
              enterTo="opacity-100 max-h-96"
              leave="transition-all duration-75 ease-out"
              leaveFrom="opacity-100 max-h-96"
              leaveTo="opacity-0 max-h-0"
            >
              <Disclosure.Panel
                static
                id={`nav-group-${group.id}-panel`}
                className="menu-group-panel"
              >
                {group.children
                  .filter((child) => !child.hidden && hasTabPermission(child))
                  .map((child) => {
                    const isActive = activeTabPath.startsWith(child.path)
                    const childBadgeValue = getBadgeValue?.(child.id)

                    return (
                      <button
                        key={child.id}
                        onClick={() => onNavigate(child.path)}
                        className={`menu-child-button ${isActive ? 'active' : ''}`}
                      >
                        <span className="menu-icon">{renderIcon(child.icon)}</span>
                        <span className="menu-label">{child.label}</span>
                        {childBadgeValue && (
                          <span className="menu-badge animate-pulse">
                            {childBadgeValue}
                          </span>
                        )}
                      </button>
                    )
                  })}
              </Disclosure.Panel>
            </Transition>
          </>
        )}
      </Disclosure>
    </li>
  )
}
